import { useState, useEffect, useRef, useCallback } from "react";
import { P, FF } from "../../styles/tokens";
import { MESES } from "../../constants/months";
import { api } from "../../services/api";
import { track } from "../../lib/posthog";
import DukyAvatar from "./DukyAvatar";

export default function DukyChat({ mesId, activeMonth, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(
    async (userMsg) => {
      if (!userMsg.trim() || loading) return;

      try { track("duky_mensaje_enviado", { caracteres: userMsg.length }); } catch {}

      const userMessage = { role: "user", content: userMsg };
      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      setInput("");
      setLoading(true);

      try {
        // El backend construye el contexto a partir del mes_id + mensaje.
        const result = await api.duky.chat(
          newMessages.map((m) => ({ role: m.role, content: m.content })),
          { mes_id: mesId, mes_label: MESES[activeMonth] || "" },
        );

        const reply = result?.reply || "¡Ups! Tuve un problemita 🐿️ Intenta de nuevo.";
        setMessages([...newMessages, { role: "assistant", content: reply }]);
      } catch (e) {
        console.error("Duky chat error:", e);
        const isTokenLimit = e.message?.includes("límite de uso");
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: isTokenLimit
              ? "Has alcanzado tu límite de uso este mes 🐿️ Se renueva el próximo mes. ¡Mientras tanto, revisa tus finanzas en el dashboard!"
              : "Parece que hay un problema de conexión 🌱 Revisa tu internet e inténtalo de nuevo.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [messages, loading, mesId, activeMonth],
  );

  // No enviar mensaje automático — dejar que el usuario elija un quick prompt

  const quickPrompts = [
    { label: "¿Me alcanza para...?", msg: "¿Me alcanza para hacer una compra extra este mes? ¿Cuánto puedo gastar sin afectar mis finanzas?" },
    { label: "¿Dónde puedo ahorrar más?", msg: "¿Dónde puedo ahorrar más?" },
    { label: "¿Cómo va mi ahorro?", msg: "¿Cómo va mi ahorro?" },
    { label: "¿Qué gasto puedo reducir?", msg: "¿Qué gasto puedo reducir?" },
    { label: "Consejo del día", msg: "Consejo del día" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", background: P.dark, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <DukyAvatar size={36} animated={!loading} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", fontFamily: FF }}>Duky</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontFamily: FF, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: loading ? P.ahorro.mid : "#4ADE80", display: "inline-block" }} />
            {loading ? "Pensando…" : "Tu compañera de finanzas"}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(255,255,255,0.10)", border: "none", color: "#fff", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          ←
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 14px 6px", display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-end" }}>
            {m.role === "assistant" && <div style={{ fontSize: 18, flexShrink: 0, marginBottom: 2 }}>🐿️</div>}
            <div
              style={{
                maxWidth: "84%", padding: "10px 14px",
                borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                background: m.role === "user" ? P.dark : P.bg,
                color: m.role === "user" ? "#fff" : P.textMd,
                fontSize: 13, fontFamily: FF, lineHeight: 1.65,
              }}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div style={{ fontSize: 18 }}>🐿️</div>
            <div style={{ background: P.bg, borderRadius: "18px 18px 18px 4px", padding: "12px 16px" }}>
              <div style={{ display: "flex", gap: 4 }}>
                {[0, 1, 2].map((j) => (
                  <div key={j} style={{ width: 7, height: 7, borderRadius: "50%", background: P.ingresos.mid, animation: `pulse 1.2s ease-in-out ${j * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick prompts */}
      {messages.length <= 1 && (
        <div style={{ padding: "0 14px 8px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {quickPrompts.map((q) => (
            <button
              key={q.label}
              onClick={() => sendMessage(q.msg)}
              disabled={loading}
              style={{
                padding: "6px 12px", borderRadius: 99, border: `1.5px solid ${P.ingresos.tint}`,
                background: P.ingresos.pastel, color: P.ingresos.text, cursor: "pointer",
                fontFamily: FF, fontSize: 11, fontWeight: 600, opacity: loading ? 0.5 : 1,
              }}
            >
              {q.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: "8px 12px 14px", flexShrink: 0, borderTop: `1.5px solid ${P.border}` }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
            placeholder="Escríbele algo a Duky…"
            style={{ flex: 1, border: `1.5px solid ${P.border}`, borderRadius: 12, background: P.bg, color: P.text, fontSize: 13, fontFamily: FF, outline: "none", padding: "10px 14px" }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            style={{
              width: 42, height: 42, borderRadius: 12, border: "none", flexShrink: 0,
              background: input.trim() ? P.dark : P.bg,
              color: input.trim() ? "#fff" : P.textXs,
              cursor: input.trim() ? "pointer" : "default",
              fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ↑
          </button>
        </div>
        <div style={{ textAlign: "center", padding: "6px 0 0", fontSize: 9, color: P.textXs, fontFamily: FF, lineHeight: 1.4 }}>
          Duky te acompaña con ideas y consejos. Tú tienes la última palabra 🐿️
        </div>
      </div>
    </div>
  );
}
